/**
 * UserTaskSummaryRow Component
 * Expandable row showing user info, task counts, and task list
 */

import { useMemo } from "react";
import { Box, Button, HStack, Icon, VStack } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router";
import { LuMessageSquare } from "react-icons/lu";
import { differenceInDays, formatDistanceToNow } from "date-fns";
import CDS from "design-system";
import RelationNameHeader from "components/RelationNameHeader";
import { BasicUser, User } from "features/User/models";
import { RelationType } from "models/v2/onboardingJourneyNetworkRelation";
import { Task } from "features/Resources/Task/models";
import { isTaskClosed, isTaskOpen } from "features/Resources/Task/helpers";
import { computeTaskStats, getTaskHealthColor } from "features/Resources/Task/helpers/stats";
import TaskCard from "./TaskCard";

interface UserTaskSummaryRowProps {
  user: User | BasicUser;
  roles?: RelationType[];
  tasks: Task[];
  includeNotStarted?: boolean;
  hideClosed?: boolean;
  isCurrentUser?: boolean;
  compact?: boolean;
  onRemind?: () => void;
  resolveHref?: string;
  onTaskClick?: (task: Task) => void;
}

type StatusDotColor = "red" | "orange" | "green" | "gray";

export function StatusBar({
  dotColor,
  ...boxProps
}: { dotColor: StatusDotColor } & Omit<React.ComponentProps<typeof Box>, "bg">) {
  const colorMap: Record<StatusDotColor, string> = {
    red: "red.300",
    orange: "orange.300",
    green: "green.300",
    gray: "gray.200",
  };

  return <Box w="5px" h="28px" borderRadius="2px" bg={colorMap[dotColor]} flexShrink={0} {...boxProps} />;
}

export default function UserTaskSummaryRow({
  user,
  roles = [],
  tasks,
  includeNotStarted = true,
  hideClosed = false,
  isCurrentUser = false,
  compact = false,
  onRemind,
  resolveHref,
  onTaskClick,
}: UserTaskSummaryRowProps) {
  // Filter tasks based on includeNotStarted and hideClosed
  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    if (!includeNotStarted) {
      filtered = filtered.filter((t) => t.status !== "upcoming");
    }
    if (hideClosed) {
      filtered = filtered.filter((t) => !isTaskClosed(t));
    }
    return filtered;
  }, [tasks, includeNotStarted, hideClosed]);

  // Calculate task counts from filtered tasks
  const taskStats = useMemo(() => computeTaskStats(filteredTasks), [filteredTasks]);
  const { openCount: activeCount, upcomingCount: futureCount, urgentCount, overdueCount } = taskStats;

  // Sort tasks: open first, then upcoming, then closed (each group sorted by due date)
  const sortedTasks = useMemo(() => {
    const sortByDueDate = (a: Task, b: Task) => {
      if (!a.dueAt) return 1;
      if (!b.dueAt) return -1;
      return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
    };

    const openTasks = filteredTasks.filter(isTaskOpen).sort(sortByDueDate);
    const upcomingTasks = filteredTasks.filter((t) => t.status === "upcoming").sort(sortByDueDate);
    const closedTasks = filteredTasks.filter(isTaskClosed).sort(sortByDueDate);

    return [...openTasks, ...upcomingTasks, ...closedTasks];
  }, [filteredTasks]);

  const lastRemindedAt = useMemo(() => {
    const reminderDates = filteredTasks.map((t) => t.lastReminderAt).filter((d): d is string => !!d);
    if (reminderDates.length === 0) return null;
    return reminderDates.reduce((latest, d) => (d > latest ? d : latest));
  }, [filteredTasks]);

  const statusDotColor = getTaskHealthColor(filteredTasks);
  const hasOpenTasks = activeCount > 0;

  const hasTasks = sortedTasks.length > 0;

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <HStack
        width="100%"
        px={4}
        py={2}
        justify="space-between"
        borderBottomWidth="1px"
        borderColor="gray.200"
      >
        {/* Left side: Status dot, avatar, name with role */}
        <HStack spacing={3}>
          <StatusBar dotColor={statusDotColor} alignSelf="center" />
          <RelationNameHeader user={user} roles={roles} size="xs" />
        </HStack>

        {/* Right side: Counts, badges, and action buttons */}
        <HStack spacing={2}>
          {(activeCount > 0 || (includeNotStarted && futureCount > 0)) && (
            <CDS.Text12 color="gray.600" whiteSpace="nowrap">
              {activeCount > 0 && (
                <>
                  {activeCount}
                  {" Open"}
                </>
              )}
              {includeNotStarted && futureCount > 0 && (
                <>
                  {activeCount > 0 && ", "}
                  {futureCount}
                  {" Not Started"}
                </>
              )}
            </CDS.Text12>
          )}
          <CDS.TaskStatusTag.UrgentCountBadge count={urgentCount} size="sm" />
          <CDS.TaskStatusTag.OverdueCountBadge count={overdueCount} size="sm" />
          {hasOpenTasks && lastRemindedAt && differenceInDays(new Date(), new Date(lastRemindedAt)) < 2 && (
            <CDS.Text10 color="gray.500" whiteSpace="nowrap">
              Reminded {formatDistanceToNow(new Date(lastRemindedAt), { addSuffix: true })}
            </CDS.Text10>
          )}
          <Box w={2} flexShrink={0} />
          {/* Remind button (for other users) */}
          {!isCurrentUser && hasOpenTasks && onRemind && (
            <Button
              size="xs"
              variant="outline"
              colorScheme="brand"
              leftIcon={<Icon as={LuMessageSquare} boxSize="3" />}
              onClick={onRemind}
              fontWeight="normal"
            >
              {compact ? "Remind" : "Send reminder"}
            </Button>
          )}

          {/* Resolve button (for current user) — navigates to My Tasks filtered by this journey */}
          {isCurrentUser && hasOpenTasks && resolveHref && (
            <Button
              as={RouterLink}
              to={resolveHref}
              size="xs"
              variant="outline"
              colorScheme="brand"
              fontWeight="normal"
            >
              {compact ? "Resolve" : "Resolve tasks"}
            </Button>
          )}
        </HStack>
      </HStack>

      <VStack spacing={1} p={3} align="stretch" bg="white">
        {hasTasks ? (
          sortedTasks.map((task) => <TaskCard key={task.id} task={task} compact onClick={onTaskClick} />)
        ) : (
          <CDS.Text12 color="gray.500" textAlign="center" py={1}>
            No open tasks 🎉
          </CDS.Text12>
        )}
      </VStack>
    </Box>
  );
}
